import React, { useState, useEffect, useContext } from 'react';
import { Button, Modal, Form, Badge, Alert } from 'react-bootstrap';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';
import DataTable from '../components/common/DataTable';
import useClasses from '../hooks/useClasses';

const Classes = () => {
  const { activeProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  const { classes, loading, error, fetchClasses, addClass, updateClass } = useClasses();

  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({ name: '', isActive: true });
  const [formError, setFormError] = useState(null);

  const isEditor = user?.type === 'admin' || 
                   activeProject?.members?.find(m => m.user === user?._id)?.role === 'editor' ||
                   activeProject?.members?.find(m => m.user === user?._id)?.role === 'owner';

  useEffect(() => {
    if (activeProject?._id) {
      fetchClasses(activeProject._id);
    }
  }, [activeProject, fetchClasses]);

  if (!activeProject) return <div className="p-5 text-center">Please select a project.</div>;

  const handleOpenModal = (cls = null) => {
    setFormError(null);
    if (cls) {
      setEditingClass(cls);
      setFormData({ name: cls.name, isActive: cls.isActive });
    } else {
      setEditingClass(null);
      setFormData({ name: '', isActive: true });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!formData.name.trim()) return setFormError('Name is required');

    let res;
    if (editingClass) {
      res = await updateClass(activeProject._id, editingClass._id, formData.name, formData.isActive);
    } else {
      res = await addClass(activeProject._id, formData.name, formData.isActive);
    }

    if (res.success) {
      setShowModal(false);
    } else {
      setFormError(res.error);
    }
  };

  const tableHeaders = [
    { label: 'Class Name' },
    { label: 'Status' },
    ...(isEditor ? [{ label: 'Actions', align: 'right' }] : [])
  ];

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Classes Center</h2>
        {isEditor && (
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="fas fa-plus mr-1"></i> Add Class
          </Button>
        )}
      </div>

      <Alert variant="info" className="mb-4">
        <i className="fas fa-info-circle mr-2"></i>
        Use **Classes** to track different segments of your business, such as multiple store locations, departments, or properties.
      </Alert>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="card shadow-sm border-0">
        <DataTable 
          headers={tableHeaders}
          data={classes} 
          loading={loading}
          emptyMessage="No classes defined yet. Add your first store or department."
          renderRow={(cls) => (
            <tr key={cls._id}>
              <td className="align-middle font-weight-bold">{cls.name}</td>
              <td className="align-middle">
                {cls.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="danger">Inactive</Badge>}
              </td>
              {isEditor && (
                <td className="text-right align-middle">
                  <Button variant="link" size="sm" onClick={() => handleOpenModal(cls)}>
                    <i className="fas fa-edit"></i> Edit
                  </Button>
                </td>
              )}
            </tr>
          )}
        />
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingClass ? 'Edit Class' : 'Add Class'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Class Name (e.g. Store A, Marketing, Property X)</Form.Label>
              <Form.Control 
                type="text" 
                value={formData.name} 
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required 
              />
            </Form.Group>
            {editingClass && (
              <Form.Group className="mb-3">
                <Form.Check 
                  type="switch"
                  id="class-isActive-switch"
                  label="Active Status"
                  checked={formData.isActive}
                  onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Classes;
